"use client";

import type { ReactNode, TextareaHTMLAttributes } from "react";
import { useEffect, useRef } from "react";
import { cn } from "../../lib";
import { EditableFieldConfirmActions } from "../custom/editable-field/actions";
import { Textarea } from "../textarea";
import { buildControlDescribedBy, fieldMixedId } from "./field-a11y";
import { FieldCharCountBadge } from "./field-char-count-badge";
import {
	FieldShell,
	type FieldLayoutHint,
	type FieldLabelVariant,
} from "./field-shell";

export type TextareaFieldSaveMode = "confirm" | "debounce" | "inline";

export interface TextareaFieldProps
	extends Omit<
		TextareaHTMLAttributes<HTMLTextAreaElement>,
		"value" | "onChange"
	> {
	id: string;
	label: string;
	value: string;
	onChange: (value: string) => void;
	description?: string;
	labelInfo?: ReactNode;
	labelInfoAriaLabel?: string;
	error?: ReactNode;
	reserveErrorSpace?: boolean;
	layoutHint?: FieldLayoutHint;
	labelVariant?: FieldLabelVariant;
	labelTrailing?: ReactNode;
	hideLabel?: boolean;
	/** Preferred FieldTypes limit; aliases `maxLength` when both set — `maxChars` wins. */
	maxChars?: number;
	minRows?: number;
	mixed?: boolean;
	mixedTooltip?: ReactNode;
	mixedPlaceholder?: string;
	presetsSlot?: ReactNode;
	/** confirm = ConfirmActions; debounce = fire onDebouncedSave after idle; inline = no chrome */
	saveMode?: TextareaFieldSaveMode;
	debounceMs?: number;
	isDirty?: boolean;
	onConfirm?: () => void;
	onCancel?: () => void;
	onDebouncedSave?: (value: string) => void;
	confirmAriaLabel?: string;
	cancelAriaLabel?: string;
	isSaving?: boolean;
	textareaInsetStart?: ReactNode;
	/**
	 * When true with an effective max, show inset-end count.
	 * Defaults to true whenever `maxChars` / `maxLength` is set.
	 */
	showCharCount?: boolean;
}

export function TextareaField({
	id,
	label,
	value,
	onChange,
	description,
	labelInfo,
	labelInfoAriaLabel,
	error,
	reserveErrorSpace = false,
	layoutHint,
	labelVariant = "default",
	labelTrailing,
	hideLabel = false,
	maxChars,
	minRows = 3,
	mixed = false,
	mixedTooltip,
	mixedPlaceholder = "Mixed values",
	presetsSlot,
	saveMode = "inline",
	debounceMs = 600,
	isDirty = false,
	onConfirm,
	onCancel,
	onDebouncedSave,
	confirmAriaLabel = "Confirm changes",
	cancelAriaLabel = "Cancel changes",
	isSaving = false,
	textareaInsetStart,
	showCharCount,
	className,
	maxLength,
	...textareaProps
}: TextareaFieldProps) {
	const mixedId = fieldMixedId(id);
	const showMixedPlaceholder = mixed && !value.trim();
	const controlDescribedBy = buildControlDescribedBy({
		id,
		error,
		hasDescription: Boolean(description),
		mixedId: mixed ? mixedId : undefined,
	});
	const debouncedSaveRef = useRef(onDebouncedSave);
	debouncedSaveRef.current = onDebouncedSave;

	const effectiveMax =
		typeof maxChars === "number"
			? maxChars
			: typeof maxLength === "number"
				? maxLength
				: undefined;
	const showCount =
		typeof effectiveMax === "number" &&
		(showCharCount === undefined ? true : Boolean(showCharCount));

	useEffect(() => {
		if (saveMode !== "debounce" || !debouncedSaveRef.current) {
			return;
		}
		const timeoutId = window.setTimeout(() => {
			debouncedSaveRef.current?.(value);
		}, debounceMs);
		return () => window.clearTimeout(timeoutId);
	}, [value, saveMode, debounceMs]);

	return (
		<FieldShell
			id={id}
			label={hideLabel ? undefined : label}
			description={description}
			labelInfo={labelInfo}
			labelInfoAriaLabel={labelInfoAriaLabel}
			error={error}
			reserveErrorSpace={reserveErrorSpace}
			layoutHint={layoutHint}
			labelVariant={labelVariant}
			labelTrailing={labelTrailing}
			describedById={mixed ? mixedId : undefined}
			className={className}
		>
			{presetsSlot}
			{mixed && mixedTooltip ? (
				<div className="mb-1 text-xs text-muted-foreground">{mixedTooltip}</div>
			) : null}
			{showMixedPlaceholder ? (
				<p id={mixedId} className="mb-1 text-xs text-muted-foreground">
					{mixedPlaceholder}
				</p>
			) : mixed ? (
				<span id={mixedId} className="sr-only">
					{mixedPlaceholder}
				</span>
			) : null}
			<div className="overflow-hidden rounded-md border border-input bg-background shadow-xs">
				<div className="relative">
					{textareaInsetStart ? (
						<div className="px-3 pt-2">{textareaInsetStart}</div>
					) : null}
					<Textarea
						id={id}
						value={showMixedPlaceholder ? "" : value}
						placeholder={
							showMixedPlaceholder
								? mixedPlaceholder
								: textareaProps.placeholder
						}
						rows={minRows}
						maxLength={effectiveMax}
						className={cn(
							"border-0 shadow-none focus-visible:ring-0",
							showCount && "pb-6 pr-3",
						)}
						onChange={(event) => onChange(event.target.value)}
						aria-label={hideLabel ? label : undefined}
						aria-invalid={Boolean(error) ? true : undefined}
						aria-describedby={controlDescribedBy}
						{...textareaProps}
					/>
					{showCount && typeof effectiveMax === "number" ? (
						<div className="pointer-events-none absolute bottom-1.5 right-2">
							<FieldCharCountBadge
								id={`${id}-char-count`}
								current={value.length}
								max={effectiveMax}
							/>
						</div>
					) : null}
				</div>
				{saveMode === "confirm" ? (
					<div className="flex items-center justify-end gap-2 border-t px-2 py-1.5">
						<EditableFieldConfirmActions
							isDirty={isDirty}
							isSaving={isSaving}
							onConfirm={onConfirm}
							onCancel={onCancel}
							confirmAriaLabel={confirmAriaLabel}
							cancelAriaLabel={cancelAriaLabel}
						/>
					</div>
				) : null}
			</div>
		</FieldShell>
	);
}
